const { batch } = require('react-redux');
const cardsApi = require('../../api/cards');
const { SHRED_CARD, SHRED_REWARD_CARD } = require('../../lootcoinBoni');
const {
  updateLootcoinBalance,
  updateLootgemBalance,
} = require('../../actions/helpers');
const { CARDS_FETCH_LIMIT } = require('../../config');
const { authSelector } = require('../../selectors');
const { cardsSelector } = require('./selectors');
const { MINUTE_MS, SECOND_MS } = require('../../time');
const { fetchCountryIfNeeded } = require('../../actions/CountryActions');

const MIN_CARDS_UPDATE = MINUTE_MS * 5;
const FETCH_CARDS_TIMEOUT = SECOND_MS * 30;

const SET_COLLECTION_OF_CARD = 'SET_COLLECTION_OF_CARD';
const UPDATE_COLLECTION_RECORDS_COUNT = 'UPDATE_COLLECTION_RECORDS_COUNT';

function updateCollectionRecordsCount(collectionRecordsDiff) {
  return {
    type: UPDATE_COLLECTION_RECORDS_COUNT,
    collectionRecordsDiff,
  };
}

function fetchCardsInProgress() {
  return {
    type: 'FETCH_CARDS_IN_PROGRESS',
    timestamp: Date.now(),
  };
}

function fetchCardsSuccessful(
  cards,
  value,
  totalRecords,
  collectionRecords,
  didInvalidate,
  all,
) {
  return {
    type: 'FETCH_CARDS_SUCCESSFUL',
    cards,
    value,
    totalRecords,
    collectionRecords,
    didInvalidate,
    all,
    timestamp: Date.now(),
  };
}

function fetchCardsFailed(reason) {
  return {
    type: 'FETCH_CARDS_FAILED',
    reason,
    timestamp: Date.now(),
  };
}

function fetchCardInProgress(cardId) {
  return {
    type: 'FETCH_CARD_IN_PROGRESS',
    cardId,
    timestamp: Date.now(),
  };
}

function fetchCardSuccessful(card, language) {
  return {
    type: 'FETCH_CARD_SUCCESSFUL',
    card,
    language,
    timestamp: Date.now(),
  };
}

function fetchCardFailed(cardId, reason) {
  return {
    type: 'FETCH_CARD_FAILED',
    reason,
    cardId,
    timestamp: Date.now(),
  };
}

function resetCards() {
  return {
    type: 'RESET_CARDS',
    timestamp: Date.now(),
  };
}

function setCollectionOfCard(cardId, collectionId) {
  return {
    type: SET_COLLECTION_OF_CARD,
    cardId,
    collectionId,
  };
}

function fetchCards({ all } = {}) {
  return async (dispatch, getState) => {
    try {
      const { apiUrl, token } = authSelector(getState());

      if (!apiUrl || !token) {
        return undefined;
      }

      dispatch(fetchCardsInProgress());

      const country = await dispatch(fetchCountryIfNeeded());
      const { didInvalidate, lastFetchedCardId } =
        cardsSelector(getState()) ?? {};

      const sliceParams = {};

      if (!all) {
        sliceParams.limit = CARDS_FETCH_LIMIT;
        if (didInvalidate) {
          sliceParams.offset = 0;
        } else {
          sliceParams.last = lastFetchedCardId;
        }
      }

      const cards = await cardsApi.fetchCards(
        apiUrl,
        token,
        sliceParams,
        country,
      );
      dispatch(
        fetchCardsSuccessful(
          cards.items,
          cards.value,
          cards.totalRecords - cards.collectionRecords,
          cards.collectionRecords,
          didInvalidate,
          all,
        ),
      );
      return cards;
    } catch (error) {
      dispatch(fetchCardsFailed(error));
      throw error;
    }
  };
}

function fetchCard(cardId) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const { apiUrl, language } = state.auth;
      const { token } = state.auth;
      if (!apiUrl || !token) {
        return undefined;
      }
      dispatch(fetchCardInProgress(cardId));
      const country = await dispatch(fetchCountryIfNeeded());

      const card = await cardsApi.fetchCard(apiUrl, token, cardId, country);
      dispatch(fetchCardSuccessful(card, language));
      return card;
    } catch (error) {
      dispatch(fetchCardFailed(cardId, error));
      throw error;
    }
  };
}

function shouldFetchCards(state, dispatch, slice) {
  const { apiUrl, token } = state.auth;

  // Only fetch purchases if logged in
  if (!apiUrl || !token) {
    return false;
  }

  const {
    fetchingInProgressSince,
    didInvalidate,
    items,
    totalRecords,
    lastSuccessfulFetch,
  } = cardsSelector(state);

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate || !lastSuccessfulFetch) {
      return true;
    }
    if (slice) {
      if (Object.keys(items).length < totalRecords) {
        return true;
      }
    } else if (checkIfOutdated(lastSuccessfulFetch)) {
      dispatch(invalidateCards());
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_CARDS_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

const checkIfOutdated = (lastSuccessfulFetch) =>
  !lastSuccessfulFetch || Date.now() - lastSuccessfulFetch > MIN_CARDS_UPDATE;

function shouldFetchCard(cardId, state, skipOutdatedCheck) {
  const { apiUrl, token, language } = state.auth;

  // Only fetch purchases if logged in
  if (!apiUrl || !token || !state.cards) {
    return false;
  }

  const {
    fetchingInProgressSince,
    didInvalidate,
    lastSuccessfulFetch,
    language: cardLanguage,
  } = state.cards.items[cardId] || {};

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate || cardLanguage !== language) {
      return true;
    }

    // Should update if data is too old
    if (!skipOutdatedCheck && checkIfOutdated(lastSuccessfulFetch)) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_CARDS_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchCardsIfNeeded({ slice } = {}) {
  return (dispatch, getState) => {
    if (shouldFetchCards(getState(), dispatch, slice)) {
      return dispatch(fetchCards());
    }
    return Promise.resolve();
  };
}

function fetchCardIfNeeded(cardId) {
  return (dispatch, getState) => {
    if (shouldFetchCard(cardId, getState())) {
      return dispatch(fetchCard(cardId));
    }
    return Promise.resolve();
  };
}

function invalidateCards() {
  return {
    type: 'INVALIDATE_CARDS',
    timestamp: Date.now(),
  };
}

function addCards(cards) {
  return {
    type: 'ADD_CARDS',
    cards,
    timestamp: Date.now(),
  };
}

function getCardRewardInProgress(cardId) {
  return {
    type: 'GET_CARD_REWARD_IN_PROGRESS',
    cardId,
    timestamp: Date.now(),
  };
}

function getCardRewardSuccessful(cardId, reward, questProgress, share) {
  return {
    type: 'GET_CARD_REWARD_SUCCESSFUL',
    cardId,
    reward,
    questProgress,
    share,
    timestamp: Date.now(),
  };
}

function sendQuestSolutionSuccessful(cardId, questProgress) {
  return {
    type: 'SEND_QUEST_SOLUTION_SUCCESSFUL',
    cardId,
    questProgress,
    timestamp: Date.now(),
  };
}

function shredCardSuccessful(cardId) {
  return {
    type: 'SHRED_CARD_SUCCESSFUL',
    cardId,
    timestamp: Date.now(),
  };
}

function shredCardsSuccessful(cardsIds, cardsNotShredded = [], invalidate) {
  return {
    type: 'SHRED_CARDS_SUCCESSFUL',
    cardsIds,
    timestamp: Date.now(),
    cardsNotShredded,
    invalidate,
  };
}

function getReward(cardId) {
  return (dispatch, getState) => {
    dispatch(getCardRewardInProgress(cardId));

    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;

    return cardsApi.getReward(apiUrl, token, cardId).then((reward) => {
      dispatch(
        getCardRewardSuccessful(
          cardId,
          { ...reward, questProgress: undefined, share: undefined },
          reward.questProgress,
          reward.share,
        ),
      );
      return reward;
    });
  };
}

function shredCard(cardId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;

    return cardsApi.shredCard(apiUrl, token, cardId).then((result) => {
      dispatch(shredCardSuccessful(cardId));
      if (result.gotBonus) {
        dispatch(updateLootcoinBalance(result.newLootcoinBalance));
        if (typeof result.newLootgemBalance === 'number') {
          dispatch(updateLootgemBalance(result));
        }
      }
      return result;
    });
  };
}

function shredCards(cardsIds, timeout = 3000) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      cards: { items: cards },
      auth: {
        token,
        apiUrl,
        user: { data: { lootcoinBalance = 0 } = {} },
      },
    } = state;

    const lootcoinBonus = cardsIds
      .map((_id) => {
        if (!cards[_id] || cards[_id].shredded) {
          return 0;
        }
        return cards[_id].reward ? SHRED_REWARD_CARD : SHRED_CARD;
      })
      .reduce((a, v) => a + v, 0);

    const shredStatus = {
      aborted: false,
      completed: false,
    };

    dispatch(updateLootcoinBalance(lootcoinBalance + lootcoinBonus));
    dispatch(shredCardsSuccessful(cardsIds, [], true));

    const revert = () => {
      batch(() => {
        dispatch(shredCardsSuccessful([], cardsIds, false));
        dispatch(updateLootcoinBalance(lootcoinBalance));
      });
    };

    const promise = new Promise((resolve, reject) => {
      setTimeout(async () => {
        if (!shredStatus.aborted) {
          shredStatus.completed = true;
          try {
            const result = await cardsApi
              .shredCards(apiUrl, token, cardsIds)
              .catch((e) => {
                revert();
                invalidateCards();
                throw e;
              });
            const {
              gotBonus,
              cardsShredded,
              newLootcoinBalance,
              newLootgemBalance,
            } = result;

            dispatch(
              shredCardsSuccessful(
                cardsShredded,
                cardsIds.filter((_id) => !cardsShredded.includes(_id)),
                false,
              ),
            );
            if (gotBonus) {
              dispatch(updateLootcoinBalance(newLootcoinBalance));
              if (typeof newLootgemBalance === 'number') {
                dispatch(updateLootgemBalance({ newLootgemBalance }));
              }
            }
            resolve(result);
          } catch (e) {
            reject(e);
          }
        } else {
          resolve(false);
        }
      }, timeout);
    });

    promise.abort = () => {
      if (!shredStatus.completed && !shredStatus.aborted) {
        shredStatus.aborted = true;
        revert();
      }
    };

    return promise;
  };
}

function sendQuestSolution(cardId, data, files) {
  return async (dispatch, getState) => {
    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;
    const country = await dispatch(fetchCountryIfNeeded());

    return cardsApi
      .sendQuestSolution(apiUrl, token, cardId, data, files, country)
      .then((questProgress) => {
        dispatch(sendQuestSolutionSuccessful(cardId, questProgress));
      });
  };
}

function setCardsSelected(cardsSelected) {
  return {
    type: 'SET_CARDS_SELECTED',
    cardsSelected,
  };
}

function setPremiumCardExchangeInfoSeen(premiumCardExchangeInfoSeen) {
  return {
    type: 'TOGGLE_PREMIUM_CARD_EXCHANGE_INFO_SEEN',
    premiumCardExchangeInfoSeen,
  };
}

function changeSortOrder(sortOrder) {
  return {
    type: 'SORT_CARDS',
    sortOrder,
  };
}

module.exports = {
  invalidateCards,
  fetchCardsIfNeeded,
  resetCards,
  fetchCardIfNeeded,
  fetchCards,
  fetchCard,
  addCards,
  getReward,
  shredCard,
  shredCardSuccessful,
  shredCards,
  shredCardsSuccessful,
  setCardsSelected,
  sendQuestSolution,
  sendQuestSolutionSuccessful,
  changeSortOrder,
  setPremiumCardExchangeInfoSeen,
  setCollectionOfCard,
  updateCollectionRecordsCount,
  SET_COLLECTION_OF_CARD,
  UPDATE_COLLECTION_RECORDS_COUNT,
};
